import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { formatDateTime } from '../../utils/helpers';
import toast from 'react-hot-toast';

export default function Followups({ caseId }) {
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    followup_date: '',
    message: '',
  });

  useEffect(() => {
    fetchFollowups();
  }, [caseId]);

  const fetchFollowups = async () => {
    try {
      setLoading(true);
      const data = await api.followups.list(caseId);
      setFollowups(Array.isArray(data) ? data : (data.followups || data.data || []));
    } catch (error) {
      toast.error('Failed to load followups');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddFollowup = async () => {
    if (!formData.followup_date || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      setLoading(true);
      await api.followups.create(caseId, formData);
      toast.success('Followup added');
      setFormData({ followup_date: '', message: '' });
      setShowForm(false);
      await fetchFollowups();
    } catch (error) {
      toast.error('Failed to add followup');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteFollowup = async (followupId) => {
    try {
      await api.followups.complete(followupId);
      toast.success('Followup marked as complete');
      await fetchFollowups();
    } catch (error) {
      toast.error('Failed to complete followup');
      console.error(error);
    }
  };

  return (
    <div className="followups-container">
      {loading ? (
        <div className="skeleton" style={{ height: '100px' }}></div>
      ) : followups.length === 0 ? (
        <p className="text-gray">No followups yet</p>
      ) : (
        <div className="followups-list">
          {followups.map((followup) => (
            <div
              key={followup.id}
              className={`followup-item ${followup.status === 'completed' ? 'completed' : ''}`}
            >
              <div className="followup-header">
                <p className="followup-date">{formatDateTime(followup.followup_date)}</p>
                <span className={`followup-status ${followup.status}`}>
                  {followup.status?.toUpperCase()}
                </span>
              </div>
              <p className="followup-message">{followup.message}</p>
              {followup.status !== 'completed' && (
                <button
                  className="btn btn-small btn-primary"
                  onClick={() => handleCompleteFollowup(followup.id)}
                >
                  Mark Complete
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="followup-form-section">
        {showForm ? (
          <div className="followup-form">
            <h3>Add New Followup</h3>
            <div className="form-group">
              <label htmlFor="followup_date" className="form-label">
                Followup Date & Time
              </label>
              <input
                id="followup_date"
                type="datetime-local"
                name="followup_date"
                value={formData.followup_date}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="message" className="form-label">
                Message
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                className="form-input"
                rows={4}
              />
            </div>

            <div className="form-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowForm(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAddFollowup}
                disabled={loading}
              >
                Add Follow Up
              </button>
            </div>
          </div>
        ) : (
          <button
            className="btn btn-primary"
            onClick={() => setShowForm(true)}
          >
            + Add Followup
          </button>
        )}
      </div>
    </div>
  );
}
